import { AttachmentDisposition, CommentAttachment } from './inboxEntryComment';

const IMAGE_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/avif',
]);

/**
 * Images accepted by the Assistant composer. Narrower than IMAGE_MIME_TYPES:
 * HEIC/HEIF/AVIF do not render in <img> on most browsers and are rejected by
 * the forwarding paths (Facebook, Bedrock).
 */
export const ASSISTANT_IMAGE_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

/**
 * What a local coding agent is handed as context for a delegation. Checked against the type S3
 * stored after the upload's magic-byte check, never against the file name. Narrower than
 * ALLOWED_ATTACHMENT_MIME_TYPES: no svg (can carry script), html, archives or office formats the
 * agent cannot read.
 */
export const DELEGATION_CONTEXT_IMAGE_MIME_TYPES: ReadonlySet<string> =
  ASSISTANT_IMAGE_MIME_TYPES;

export const DELEGATION_CONTEXT_MIME_TYPES: ReadonlySet<string> = new Set([
  ...DELEGATION_CONTEXT_IMAGE_MIME_TYPES,
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/markdown',
]);

/** Lowercases and drops parameters: `Image/PNG; charset=binary` -> `image/png`. */
export function canonicalMimeType(contentType: string | undefined): string {
  return (contentType ?? '').split(';')[0].trim().toLowerCase();
}

export function isDelegationContextMimeType(
  contentType: string | undefined,
): boolean {
  return DELEGATION_CONTEXT_MIME_TYPES.has(canonicalMimeType(contentType));
}

export function isDelegationContextImageMimeType(
  contentType: string | undefined,
): boolean {
  return DELEGATION_CONTEXT_IMAGE_MIME_TYPES.has(
    canonicalMimeType(contentType),
  );
}

const ODF_MIME_TYPES: ReadonlySet<string> = new Set([
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
  'application/vnd.oasis.opendocument.graphics',
]);

const DOCUMENT_MIME_TYPES: ReadonlySet<string> = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ...ODF_MIME_TYPES,
]);

const TEXT_MIME_TYPES: ReadonlySet<string> = new Set([
  'text/plain',
  'text/csv',
  'text/markdown',
]);

const ARCHIVE_MIME_TYPES: ReadonlySet<string> = new Set([
  'application/zip',
  'application/x-zip-compressed',
]);

const AUDIO_MIME_TYPES: ReadonlySet<string> = new Set(['audio/mpeg']);

const VIDEO_MIME_TYPES: ReadonlySet<string> = new Set([
  'video/mp4',
  'video/webm',
]);

export const ALLOWED_ATTACHMENT_MIME_TYPES: ReadonlySet<string> = new Set([
  ...IMAGE_MIME_TYPES,
  ...DOCUMENT_MIME_TYPES,
  ...TEXT_MIME_TYPES,
  ...ARCHIVE_MIME_TYPES,
  ...AUDIO_MIME_TYPES,
  ...VIDEO_MIME_TYPES,
]);

export const INLINE_VIEWABLE_MIME_TYPES: ReadonlySet<string> = new Set([
  ...IMAGE_MIME_TYPES,
  ...TEXT_MIME_TYPES,
  ...AUDIO_MIME_TYPES,
  ...VIDEO_MIME_TYPES,
  'application/pdf',
]);

const EXTENSION_TO_MIME_TYPE: Readonly<Record<string, string>> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  avif: 'image/avif',
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  csv: 'text/csv',
  md: 'text/markdown',
  markdown: 'text/markdown',
  zip: 'application/zip',
  odt: 'application/vnd.oasis.opendocument.text',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  odp: 'application/vnd.oasis.opendocument.presentation',
  odg: 'application/vnd.oasis.opendocument.graphics',
  mp3: 'audio/mpeg',
  mp4: 'video/mp4',
  webm: 'video/webm',
};

export function isInlineViewableFileName(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (!ext) return false;
  const mimeType = EXTENSION_TO_MIME_TYPE[ext];
  return mimeType !== undefined && INLINE_VIEWABLE_MIME_TYPES.has(mimeType);
}

/** Best-effort MIME type guess from a file name's extension; `undefined` when unknown. */
export function getMimeTypeFromFileName(fileName: string): string | undefined {
  const ext = fileName.split('.').pop()?.toLowerCase();
  return ext ? EXTENSION_TO_MIME_TYPE[ext] : undefined;
}

// MIME types not covered by the `text/*` prefix that are still plain, readable text.
const NON_TEXT_PREFIX_READABLE_MIME_TYPES: ReadonlySet<string> = new Set([
  'application/json',
  'application/xml',
]);

/** True for any MIME type whose bytes are safe to decode and display as UTF-8 text. */
export function isReadableTextMimeType(mimeType: string): boolean {
  return (
    mimeType.startsWith('text/') ||
    NON_TEXT_PREFIX_READABLE_MIME_TYPES.has(mimeType)
  );
}

export function getAttachmentDispositionByMimeType(
  mimeType: string,
): AttachmentDisposition {
  return INLINE_VIEWABLE_MIME_TYPES.has(mimeType)
    ? AttachmentDisposition.INLINE
    : AttachmentDisposition.ATTACHMENT;
}

export function getAttachmentDispositionFromFileName(
  attachment: CommentAttachment,
): AttachmentDisposition {
  return isInlineViewableFileName(attachment.fileName)
    ? AttachmentDisposition.INLINE
    : AttachmentDisposition.ATTACHMENT;
}

export function isInlineDisposition(attachment: CommentAttachment): boolean {
  return attachment.disposition
    ? attachment.disposition === AttachmentDisposition.INLINE
    : isInlineViewableFileName(attachment.fileName);
}
